import { FileUploader } from "react-drag-drop-files";

import {
  AddIcon,
  ChevronRightIcon,
  InfoIcon,
  SearchIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Input,
  InputRightElement,
  VStack,
  HStack,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  InputGroup,
  Flex,
  Divider,
  Image,
  Checkbox,
  Tooltip,
  Heading,
} from "@chakra-ui/react";
import "./BrokerUpload.css";
import SearchItem from "../../components/SearchItem";
import FileUpload from "../../components/FileUploadIcon";
import { TastyWorksInstructions } from "./instructions";
import FidelityInstructions, {
  FidelityEmailForTradeConfirmations,
} from "./instructions/FidelityInstructions";

import { useBrokerUploadController } from "./useBrokerUploadController";
import { GoBack } from "../../components/GoBack";
import {
  SchwabEmailForTradeConfirmations,
  SchwabInstructions,
} from "./instructions/SchwabInstructions";
import InteractiveBrokersInstructions from "./instructions/InteractiveBrokersInstructions";
import { Spinner } from "../../../../components";
import { DropDown } from "../../../../components/design_library";
import { useAtomValue } from "jotai";
import { AccountAtom, TransactionsAtom } from "../../../../store";
import { useTour } from "@reactour/tour";
import ETradeInstructions from "./instructions/eTradeInstructions";
import React, { useEffect, useRef } from "react";
import { AutoSync } from "../../../AutoSync/AutoSync";
import InteractiveBrokersAutoSyncInstructions from "./instructions/InteractiveBrokersAutoSyncInstructions";
import { HiDeviceMobile } from "react-icons/hi";
import { RiEmotionUnhappyFill } from "react-icons/ri";
import ETradeAutoSyncInstructions from "./instructions/EtradeAutoSyncInstructions";
import RobinhoodInstructions from "./instructions/RobinhoodInstructions";
import moment from "moment";
import GenericInstructions from "./instructions/GenericInstructions";
import FirstradeInstructions from "./instructions/FirstradeInstructions";
import AllyInstructions from "./instructions/AllyInstructions";
import { SchwabAutoSyncInstructions } from "./instructions/SchwabAutoSyncInstructions";
import SCHWAB_AutoSync from "../../../AutoSync/Brokers/SCHWAB/SCHWAB_AutoSync";
import { useTransactions } from "../../../../hooks/useTransactions";
import AIUpload from "../AIUpload/AIUpload";

const defaultFileTypes = ["CSV"];
const etradeFileTypes = ["CSV", "XLSX"];
const aiUploadFileTypes = ["CSV", "XLSX"];

// Get file types based on selected broker
const getFileTypesForBroker = (broker: string): string[] => {
  if (broker === "ETRADE") {
    return etradeFileTypes;
  }
  if (broker === "AI_UPLOAD") {
    return aiUploadFileTypes;
  }
  return defaultFileTypes;
};

const BrokerUpload = (props: any) => {
  const controller = useBrokerUploadController(props);
  const transactionsState = useAtomValue(TransactionsAtom);
  const accountState = useAtomValue(AccountAtom);
  const transactions = useTransactions();
  const books = transactionsState.books.map((book: any) => {
    return { value: book, label: book };
  });

  const { isOpen, currentStep, steps, setIsOpen, setCurrentStep, setSteps } =
    useTour();

  const hasTrades = transactionsState.trades.length > 0;
  const lastAccountIdRef = useRef<number | null>(null);

  // Refresh books when component mounts or when account actually changes
  useEffect(() => {
    const accountId = controller.selectedAccountForUpload?.id || accountState.selectedAccount?.id;
    if (accountId && accountId !== lastAccountIdRef.current) {
      lastAccountIdRef.current = accountId;
      transactions.getBooks(accountId);
    }
  }, [controller.selectedAccountForUpload?.id, accountState.selectedAccount?.id]);

  return (
    <Box className="tutorial-modal-upload" minH="480px">
      <VStack
        alignItems="flex-start"
        justifyContent={"flex-start"}
        mt="10px"
        mb="12px"
      >
        {!controller.selectedBroker && props.goBackButton}

        <HStack justifyContent={"space-between"} w={"100%"}>
          {controller.selectedBroker && (
            <Box pb="20px">
              <GoBack
                onClick={() => {
                  controller.setSelectedBroker("");
                  controller.setErrorMessage("");
                  controller.setSuccessfulUpload(false);
                }}
              >
                Go back to import selection
              </GoBack>
            </Box>
          )}

          <HStack>
            {controller.selectedBroker && (
              <VStack alignItems={"flex-end"} mt="10px" mr="20px">
                <Text
                  fontSize="xs"
                  lineHeight={0}
                  fontWeight={500}
                  color="var(--light-gray)"
                >
                  Last upload
                </Text>
                <Text fontSize="sm" opacity="0.8">
                  {controller.dateOfLastUpload
                    ? moment(controller.dateOfLastUpload).calendar()
                    : "No uploads yet"}
                </Text>
              </VStack>
            )}
            {controller.selectedBroker && (
              <VStack alignItems={"flex-end"} mt="10px">
                <Text
                  fontSize="xs"
                  lineHeight={0}
                  fontWeight={500}
                  color="var(--light-gray)"
                >
                  Import portfolio
                </Text>
                <Text fontSize="sm" opacity="0.8">
                  {controller.selectedAccountForUpload?.name || accountState.selectedAccount?.name}
                </Text>
              </VStack>
            )}
          </HStack>
        </HStack>
      </VStack>

      {controller.loading && (
        <VStack h="200px" justifyContent={"center"}>
          <Spinner size="xl" />
          <Text fontSize="xl" fontWeight={600} mt="4px">
            Retrieving brokers...
          </Text>
        </VStack>
      )}

      {!controller.successfulUpload && (
        <>
          <HStack wrap={"wrap"}>
            {!controller.loading && (
              <Box w="100%" maxW="100%" minW={0} overflow="hidden">
                {controller.selectedBroker === "AI_UPLOAD" && (
                  <AIUpload
                    isModalOpen={props.isOpen}
                    closeModal={props.closeModal}
                    selectedBook={controller.selectedBook}
                    setSelectedBook={controller.setSelectedBook}
                    calculateStrategy={controller.calculateStrategy}
                    setCalculateStrategy={controller.setCalculateStrategy}
                    selectedAccountForUpload={controller.selectedAccountForUpload}
                    setSelectedAccountForUpload={
                      controller.setSelectedAccountForUpload
                    }
                  />
                )}
                {controller.selectedBroker &&
                  controller.selectedBroker !== "AI_UPLOAD" && (
                  <Box
                    onClick={() => {
                      controller.setSelectedBroker("");
                    }}
                  >
                    <FileUploader
                      handleChange={(file: any) => {
                        controller.uploadFile(file);
                      }}
                      enctype="multipart/form-data"
                      name="file"
                      types={getFileTypesForBroker(controller.selectedBroker)}
                      disableDragAndDrop={true}
                      children={
                        <Box
                          maxW="100%"
                          maxH="100%"
                          display={"flex"}
                          justifyContent={"center"}
                          alignItems={"center"}
                          flexDir={"column"}
                          className={"drop-zone"}
                          background="var(--dark-gray)"
                          border="4px dashed var(--gray)"
                          borderRadius={"8px"}
                          py="60px"
                          cursor={"pointer"}
                          mb="10px"
                        >
                          {controller.uploading && (
                            <Box
                              display="flex"
                              justifyContent={"center"}
                              alignItems={"center"}
                              flexDir={"column"}
                              mb="10px"
                            >
                              <Spinner size="xl" mb="12px" />
                              <Text>Uploading...</Text>
                            </Box>
                          )}
                          {!controller.uploading && (
                            <>
                              {!controller.errorMessage ? (
                                <FileUpload h="40px" w="40px" />
                              ) : (
                                <WarningIcon
                                  color="var(--red)"
                                  h="40px"
                                  w="40px"
                                />
                              )}
                              <Text fontWeight={600} mt="2">
                                {controller.selectedBroker}
                              </Text>
                              {controller.errorMessage ? (
                                <Text
                                  fontWeight={500}
                                  fontSize={"md"}
                                  color="var(--red)"
                                >
                                  {controller.errorMessage}
                                </Text>
                              ) : (
                                <Text fontWeight={500} fontSize={"sm"}>
                                  Drag & drop file to upload or click on this
                                  box
                                </Text>
                              )}
                              <Text fontWeight={500} fontSize={"xs"} pt="4px">
                                Supported format: {getFileTypesForBroker(controller.selectedBroker).join(", ")}
                              </Text>
                            </>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                )}

                {!controller.successfulUpload && (
                  <>
                    {!controller.loading && (
                      <>
                        {controller.selectedBroker &&
                          controller.selectedBroker !== "AI_UPLOAD" && (
                          <Box>
                            <HStack>
                              <HStack w="full">
                                <VStack alignItems={"flex-start"}>
                                  <HStack>
                                    <Heading size="xs" fontWeight={400}>
                                      Calculate strategy
                                    </Heading>

                                    <Tooltip
                                      label="
                                      Automatically calculate the strategy based on the trades uploaded. You can either assign this value to the position group or strategy field (or neither)."
                                    >
                                      <InfoIcon />
                                    </Tooltip>
                                  </HStack>
                                  <DropDown
                                    value={{
                                      label: controller.calculateStrategy.label,
                                      value: controller.calculateStrategy.value,
                                    }}
                                    onChange={(e: any) => {
                                      controller.setCalculateStrategy(e);
                                    }}
                                    options={[
                                      {
                                        value: "NONE",
                                        label: "Do not generate",
                                      },
                                      {
                                        value: "STRATEGY",
                                        label: "Strategy field",
                                      },
                                      {
                                        value: "POSITION_GROUP",
                                        label: "Position Group field",
                                      },
                                      {
                                        value: "BOOK",
                                        label: "Book field",
                                      },
                                    ]}
                                    placeholder={"Selected strategy field"}
                                  />
                                </VStack>

                                <VStack alignItems={"flex-start"}>
                                  <HStack>
                                    <Heading size="xs" fontWeight={400}>
                                      Import to portfolio
                                    </Heading>
                                    <Tooltip label="Select which portfolio to import the trades into. Defaults to the currently selected portfolio.">
                                      <InfoIcon />
                                    </Tooltip>
                                  </HStack>
                                  <DropDown
                                    value={{
                                      value: controller.selectedAccountForUpload?.id || "",
                                      label: controller.selectedAccountForUpload?.name || "Select portfolio",
                                    }}
                                    onChange={(e: any) => {
                                      const selectedAccount = accountState.accounts.find(acc => acc.id === e.value);
                                      controller.setSelectedAccountForUpload(selectedAccount);
                                    }}
                                    options={accountState.accounts.map((account: any) => ({
                                      value: account.id,
                                      label: account.name,
                                    }))}
                                    placeholder={"Select portfolio"}
                                  />
                                </VStack>

                                <VStack>
                                  <HStack w={"full"}>
                                    <Heading size="xs" fontWeight={400}>
                                      Assign trades to book
                                    </Heading>
                                    <Tooltip label="Assign the trades uploaded to a specific book. If no book is selected, the trades will be unbooked.">
                                      <InfoIcon />
                                    </Tooltip>
                                  </HStack>

                                  <DropDown
                                    value={{
                                      value: controller.selectedBook,
                                      label:
                                        controller.selectedBook ===
                                          "undefined" ||
                                        controller.selectedBook === ""
                                          ? "No book selected"
                                          : controller.selectedBook,
                                    }}
                                    onChange={(e: any) => {
                                      controller.setSelectedBook(e.value);
                                    }}
                                    options={[
                                      {
                                        value: "undefined",
                                        label: "No book selected",
                                      },
                                      ...books,
                                    ]}
                                    placeholder={"No book selected"}
                                  />
                                </VStack>
                              </HStack>
                            </HStack>

                            <Divider mt="20px" mb="20px" />
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}

                {!controller.selectedBroker && (
                  <Box pb="36px">
                    <InputGroup>
                      <Input
                        onChange={(e) => {
                          controller.search(e.target.value);
                        }}
                        value={controller.textSearch}
                        placeholder={`Search by broker name`}
                      />
                      <InputRightElement>
                        <SearchIcon />
                      </InputRightElement>
                    </InputGroup>
                    <VStack
                      alignItems={"flex-start"}
                      // maxH={"540px"}
                      overflowY="scroll"
                      mt="10px"
                      sx={{
                        html: {
                          "scrollbar-face-color": "var(--bg)",
                          "scrollbar-base-color": "var(--bg)",
                          "scrollbar-3dlight-color": "var(--bg)",
                          "scrollbar-highlight-color": "var(--bg)",
                          "scrollbar-track-color": "blue",
                          "scrollbar-arrow-color": "blue",
                          "scrollbar-shadow-color": "var(--bg)",
                          "scrollbar-dark-shadow-color": "var(--bg)",
                        },

                        "&::-webkit-scrollbar": {
                          width: "0px",
                          height: "0px",
                          display: "none",
                        },
                        "&::-webkit-scrollbar-button": {
                          "background-color": "transparent",
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          "background-color": "var(--bg)",
                        },
                        "&::-webkit-scrollbar-track-piece": {
                          "background-color": "var(--gray)",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          height: "50px",
                          "background-color": "#666",
                          "border-radius": "3px",
                        },
                        "&::-webkit-scrollbar-corner": {
                          "background-color": "var(--bg)",
                        },
                        "&::-webkit-resizer": {
                          "background-color": "#666",
                        },
                      }}
                    >
                      {controller.textSearch.length > 0 &&
                        controller.filteredResults.map((item: any) => {
                          return (
                            <SearchItem
                              item={item}
                              setSelectedbroker={controller.setSelectedBroker}
                            />
                          );
                        })}

                      {controller.textSearch.length === 0 &&
                        controller.dataList.map((item: any) => {
                          return (
                            <SearchItem
                              item={item}
                              setSelectedbroker={controller.setSelectedBroker}
                            />
                          );
                        })}
                      {controller.textSearch.length > 0 &&
                        controller.filteredResults.length === 0 && (
                          <Box
                            className={"uploadAddTradeSearchRow"}
                            width="100%"
                            background="var(--dark-gray)"
                            border="1px solid var(--gray)"
                            borderRadius={"8px"}
                            padding="12px"
                            bg="var(--black)"
                          >
                            <HStack justifyContent={"space-between"}>
                              <HStack>
                                <Box onClick={() => {}}>
                                  <Text fontSize="20px" fontWeight={500}>
                                    We haven't found that broker.
                                  </Text>
                                  <Text
                                    fontSize="17px"
                                    color="var(--semi-white)"
                                    fontWeight={400}
                                  >
                                    Please message support and we will add
                                    support.
                                  </Text>
                                </Box>
                              </HStack>
                            </HStack>
                          </Box>
                        )}

                      <Box
                        width="100%"
                        background="var(--dark-gray)"
                        border="1px solid var(--gray)"
                        borderRadius={"8px"}
                        padding="12px"
                        bg="var(--bg)"
                        onClick={() => {
                          window.$crisp.push(["do", "chat:open"]);
                          window.$crisp.push([
                            "do",
                            "message:send",
                            [
                              "text",
                              `Hi! I'd like to get help with uploading my trades! My requested broker is "${controller.textSearch}"`,
                            ],
                          ]);
                        }}
                        cursor={"pointer"}
                      >
                        <HStack justifyContent={"space-between"}>
                          <HStack>
                            <SearchIcon w="40px" h="40px" />
                            <Box>
                              <Text fontSize="20px" fontWeight={500}>
                                Don't see your broker?
                              </Text>
                              <Text
                                fontSize="17px"
                                color="var(--semi-white)"
                                fontWeight={400}
                              >
                                Click here to request its addition.
                              </Text>
                            </Box>
                          </HStack>
                          <ChevronRightIcon w="40px" h="40px" />
                        </HStack>
                      </Box>
                    </VStack>
                  </Box>
                )}
              </Box>
            )}

            {controller.selectedBroker === "TASTYWORKS" && (
              <TastyWorksInstructions />
            )}
            {controller.selectedBroker === "FIDELITY" && (
              <Box>
                <Tabs isFitted>
                  <TabList>
                    <Tab>Historic Uploads</Tab>
                  </TabList>

                  <TabPanels>
                    <TabPanel>
                      <FidelityInstructions />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            )}
            {controller.selectedBroker === "SCHWAB" && (
              <Box>
                <Tabs isFitted>
                  <TabList>
                    <Tab>Historic Uploads</Tab>
                    <Tab>Automatic Updates</Tab>
                  </TabList>

                  <TabPanels>
                    <TabPanel>
                      <SchwabInstructions />
                    </TabPanel>
                    <TabPanel>
                      <AutoSync />
                      <Divider my="4" />
                      <SchwabAutoSyncInstructions />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            )}

            {controller.selectedBroker === "ROBINHOOD" && (
              <Box>
                <Tabs isFitted>
                  <TabList>
                    <Tab>Historic Uploads</Tab>
                  </TabList>

                  <TabPanels>
                    <TabPanel>
                      <RobinhoodInstructions />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            )}
            {controller.selectedBroker === "FIRSTRADE" && (
              <Box>
                <Tabs isFitted>
                  <TabList>
                    <Tab>Historic Uploads</Tab>
                  </TabList>

                  <TabPanels>
                    <TabPanel>
                      <FirstradeInstructions />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            )}
            {controller.selectedBroker === "ALLY" && (
              <Box>
                <Tabs isFitted>
                  <TabList>
                    <Tab>Historic Uploads</Tab>
                  </TabList>

                  <TabPanels>
                    <TabPanel>
                      <AllyInstructions />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            )}
            {controller.selectedBroker === "GENERIC" && (
              <Box>
                <GenericInstructions />
              </Box>
            )}
            {controller.selectedBroker === "INTERACTIVEBROKERS" && (
              <Tabs w="full">
                <TabList>
                  <Tab>Historic Uploads</Tab>
                  <Tab>Auto Sync</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <InteractiveBrokersInstructions />
                  </TabPanel>
                  <TabPanel>
                    <Flex w="full" direction="column">
                      <AutoSync />
                      {/* <Divider my={4} /> */}
                      {/* <InteractiveBrokersAutoSyncInstructions /> */}
                    </Flex>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
            {controller.selectedBroker === "ETRADE" && (
              <Tabs w="full">
                <TabList>
                  <Tab>Historic Uploads</Tab>
                  <Tab>Auto Sync</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <ETradeInstructions />
                  </TabPanel>
                  <TabPanel>
                    <Flex w="full" direction="column">
                      <AutoSync />
                      <Divider my={4} />
                      <ETradeAutoSyncInstructions />
                    </Flex>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </HStack>
        </>
      )}
    </Box>
  );
};

export default BrokerUpload;
